import { Controller } from "@hotwired/stimulus"

/**
 * TicketFilterController
 * 
 * Client-side filtering for Bike Hunter view ONLY.
 * Provides instant filtering without HTTP requests.
 * 
 * IMPORTANT: This controller is ONLY used when vista=bike_hunter.
 * For "Todos los tickets" view, filtering uses Turbo (server-side) instead.
 * See: app/views/apps/home_repairs/tickets/index.html.erb
 * 
 * Filters tickets by:
 * - Estado (abierto/cerrado/todos)
 * - Pipeline (Asistencia a domicilio / Internal / Taller)
 * - Search query (matches: subject, cliente, direccion, email, telefono, etc.)
 * 
 * Statistics are calculated locally in real-time based on visible tickets only.
 * No server requests for filtering or statistics updates.
 */
export default class extends Controller {
  static targets = ["ticket", "estadoButton", "pipelineButton", "searchInput", "noResults", "totalCount", "asistenciaCount", "internalCount", "tallerCount"]
  static values = {
    activeEstado: { type: String, default: "abiertos" },
    activePipeline: { type: String, default: "" }
  }

  connect() {
    console.log("🔍 TicketFilter: Connected - JavaScript filtering active")
    console.log("🔍 TicketFilter: Element:", this.element)
    console.log("🔍 TicketFilter: Initial ticketTargets count:", this.ticketTargets.length)
    console.log("🔍 TicketFilter: hasSearchInputTarget:", this.hasSearchInputTarget)
    
    // For bike_hunter, tickets are already in DOM (no lazy loading)
    // We need to re-check after a short delay to ensure DOM is fully rendered
    setTimeout(() => {
      console.log("🔍 TicketFilter: After delay ticketTargets count:", this.ticketTargets.length)
      this.updateButtonStyles()
      this.filter()
    }, 100)
    
    // Listen for Turbo frame updates (tickets loading)
    const vistaFrame = document.getElementById("vista")
    if (vistaFrame) {
      vistaFrame.addEventListener("turbo:frame-load", () => {
        console.log("🔍 TicketFilter: Vista frame loaded, re-filtering...")
        this.updateButtonStyles()
        this.filter()
      })
    }
    
    this.updateButtonStyles()
    this.filter()
  }

  /**
   * Filter by estado (abiertos/cerrado/todos)
   */
  filterEstado(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.activeEstadoValue = button.dataset.estado
    this.updateButtonStyles()
    this.filter()
  }

  /**
   * Filter by pipeline
   */
  filterPipeline(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.activePipelineValue = button.dataset.pipeline || ""
    this.updateButtonStyles()
    this.filter()
  }

  /**
   * Filter by search query
   */
  filterSearch(event) {
    console.log("🔍 TicketFilter: filterSearch() called from event:", event)
    console.log("🔍 TicketFilter: hasSearchInputTarget:", this.hasSearchInputTarget)
    if (this.hasSearchInputTarget) {
      console.log("🔍 TicketFilter: searchInput element:", this.searchInputTarget)
      console.log("🔍 TicketFilter: searchInput value:", this.searchInputTarget.value)
    } else {
      console.error("❌ TicketFilter: searchInputTarget NOT found")
      console.log("🔍 Available targets:", Object.keys(this).filter(k => k.includes('Target')))
    }
    this.filter()
  }

  /**
   * Main filter logic - combines all filters
   * Updates visibility and calculates statistics locally
   */
  filter() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase().trim() : ""
    const estado = this.activeEstadoValue
    const pipeline = this.activePipelineValue
    
    console.log("🔍 TicketFilter: filter() called")
    console.log("🔍 TicketFilter: hasSearchInputTarget:", this.hasSearchInputTarget)
    console.log("🔍 TicketFilter: query:", query, "estado:", estado, "pipeline:", pipeline)
    console.log("🔍 TicketFilter: ticketTargets count:", this.ticketTargets.length)
    
    let visibleCount = 0
    let asistenciaCount = 0
    let internalCount = 0
    let tallerCount = 0
    
    this.ticketTargets.forEach(ticket => {
      const matchesSearch = this.matchesSearchQuery(ticket, query)
      const matchesEstado = this.matchesEstado(ticket, estado)
      const matchesPipeline = this.matchesPipeline(ticket, pipeline)
      
      const isVisible = matchesSearch && matchesEstado && matchesPipeline
      ticket.classList.toggle("hidden", !isVisible)
      
      if (isVisible) {
        visibleCount++
        const ticketPipeline = ticket.dataset.pipeline || ""
        if (ticketPipeline === "Asistencia a domicilio") asistenciaCount++
        else if (ticketPipeline === "Internal") internalCount++
        else if (ticketPipeline === "Taller") tallerCount++
      }
    })
    
    // Update statistics locally (no server request)
    this.updateStatistics(visibleCount, asistenciaCount, internalCount, tallerCount)
    
    // Show/hide "no results" message
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("hidden", visibleCount > 0)
    }
    
    console.log(`🔍 TicketFilter: ${visibleCount}/${this.ticketTargets.length} tickets visible (query: "${query}")`)
    console.log(`🔍 TicketFilter: Stats - Asistencia: ${asistenciaCount}, Internal: ${internalCount}, Taller: ${tallerCount}`)
  }

  /**
   * Update statistics locally (client-side only, no server request)
   */
  updateStatistics(total, asistencia, internal, taller) {
    if (this.hasTotalCountTarget) {
      this.totalCountTarget.textContent = total
    }
    if (this.hasAsistenciaCountTarget) {
      this.asistenciaCountTarget.textContent = asistencia
    }
    if (this.hasInternalCountTarget) {
      this.internalCountTarget.textContent = internal
    }
    if (this.hasTallerCountTarget) {
      this.tallerCountTarget.textContent = taller
    }
  }

  /**
   * Check if ticket matches search query
   */
  matchesSearchQuery(ticket, query) {
    if (!query) return true
    
    const searchableText = [
      ticket.dataset.subject || "",
      ticket.dataset.cliente || "",
      ticket.dataset.direccion || "",
      ticket.dataset.ticketId || ticket.getAttribute('data-ticket-id') || "", // Support for data-ticket-id
      ticket.dataset.email || "",
      ticket.dataset.telefono || "",
      ticket.dataset.pipeline || "",
      ticket.dataset.estado || "",
      ticket.dataset.resumen || ""
    ].join(' ').toLowerCase()
    
    return searchableText.includes(query)
  }

  /**
   * Check if ticket matches estado filter
   */
  matchesEstado(ticket, estado) {
    if (!estado || estado === "todos") return true
    
    const ticketEstado = ticket.dataset.estado || ""
    
    if (estado === "abiertos") {
      // Abiertos = no cerrado
      return ticketEstado !== "cerrado"
    } else if (estado === "cerrado") {
      return ticketEstado === "cerrado"
    }
    
    return true
  }

  /**
   * Check if ticket matches pipeline filter
   */
  matchesPipeline(ticket, pipeline) {
    if (!pipeline) return true
    
    const ticketPipeline = ticket.dataset.pipeline || ""
    return ticketPipeline === pipeline
  }

  /**
   * Update button styles based on active filters
   */
  updateButtonStyles() {
    // Update estado buttons
    this.estadoButtonTargets.forEach(button => {
      const isActive = button.dataset.estado === this.activeEstadoValue
      button.classList.toggle("bg-blue-50", isActive)
      button.classList.toggle("text-blue-800", isActive)
      button.classList.toggle("ring-1", isActive)
      button.classList.toggle("ring-blue-400", isActive)
      button.classList.toggle("text-gray-700", !isActive)
      button.classList.toggle("hover:bg-gray-50", !isActive)
    })

    // Update pipeline buttons
    this.pipelineButtonTargets.forEach(button => {
      const buttonPipeline = button.dataset.pipeline || ""
      const isActive = buttonPipeline === this.activePipelineValue
      button.classList.toggle("bg-blue-50", isActive)
      button.classList.toggle("text-blue-800", isActive)
      button.classList.toggle("ring-1", isActive)
      button.classList.toggle("ring-blue-400", isActive)
      button.classList.toggle("text-gray-700", !isActive)
      button.classList.toggle("hover:bg-gray-50", !isActive)
    })
  }

  /**
   * Clear search input and refilter
   */
  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.filter()
    }
  }
}
